import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
  FC,
} from 'react';
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
  X,
} from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
}

interface ToastContextType {
  toast: (options: Omit<ToastItem, 'id'>) => string;
  success: (message: string, title?: string) => string;
  error: (message: string, title?: string) => string;
  warning: (message: string, title?: string) => string;
  info: (message: string, title?: string) => string;
  dismiss: (id: string) => void;
  clearAll: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const toastConfig: Record<
  ToastType,
  {
    icon: FC<{ className?: string }>;
    containerClass: string;
    iconColor: string;
  }
> = {
  success: {
    icon: CheckCircle2,
    containerClass:
      'border-emerald-500/30 bg-surface-panel-light dark:bg-surface-panel-dark text-content-main-light dark:text-content-main-dark',
    iconColor: 'text-emerald-500',
  },
  error: {
    icon: AlertCircle,
    containerClass:
      'border-red-500/30 bg-surface-panel-light dark:bg-surface-panel-dark text-content-main-light dark:text-content-main-dark',
    iconColor: 'text-red-500',
  },
  warning: {
    icon: AlertTriangle,
    containerClass:
      'border-amber-500/30 bg-surface-panel-light dark:bg-surface-panel-dark text-content-main-light dark:text-content-main-dark',
    iconColor: 'text-amber-500',
  },
  info: {
    icon: Info,
    containerClass:
      'border-sky-500/30 bg-surface-panel-light dark:bg-surface-panel-dark text-content-main-light dark:text-content-main-dark',
    iconColor: 'text-sky-500',
  },
};

export const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  const toast = useCallback(
    ({
      type,
      title,
      message,
      duration = 4000,
    }: Omit<ToastItem, 'id'>): string => {
      const id = Math.random().toString(36).substring(2, 9);
      const newToast: ToastItem = { id, type, title, message, duration };

      setToasts((prev) => [...prev, newToast]);

      if (duration > 0) {
        setTimeout(() => {
          dismiss(id);
        }, duration);
      }

      return id;
    },
    [dismiss]
  );

  const success = useCallback(
    (message: string, title?: string) =>
      toast({ type: 'success', message, title }),
    [toast]
  );

  const error = useCallback(
    (message: string, title?: string) =>
      toast({ type: 'error', message, title }),
    [toast]
  );

  const warning = useCallback(
    (message: string, title?: string) =>
      toast({ type: 'warning', message, title }),
    [toast]
  );

  const info = useCallback(
    (message: string, title?: string) =>
      toast({ type: 'info', message, title }),
    [toast]
  );

  const value = useMemo(
    () => ({ toast, success, error, warning, info, dismiss, clearAll }),
    [toast, success, error, warning, info, dismiss, clearAll]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2"
      >
        {toasts.map((item) => {
          const cfg = toastConfig[item.type];
          const Icon = cfg.icon;

          return (
            <div
              key={item.id}
              role="alert"
              className="animate-in fade-in slide-in-from-bottom-2 pointer-events-auto flex items-start gap-2.5 rounded-xl border border-surface-border-light bg-surface-panel-light p-3 text-xs shadow-xl transition-all dark:border-surface-border-dark dark:bg-surface-panel-dark"
            >
              <Icon
                className={`mt-0.5 h-4 w-4 flex-shrink-0 ${cfg.iconColor}`}
              />
              <div className="min-w-0 flex-1">
                {item.title && (
                  <h4 className="font-semibold leading-tight text-content-main-light dark:text-content-main-dark">
                    {item.title}
                  </h4>
                )}
                <p className="mt-0.5 leading-relaxed text-content-muted-light dark:text-content-muted-dark">
                  {item.message}
                </p>
              </div>
              <button
                onClick={() => dismiss(item.id)}
                className="cursor-pointer rounded p-0.5 text-content-muted-light transition-colors hover:text-content-main-light dark:text-content-muted-dark dark:hover:text-content-main-dark"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};
